/**
 * Workspace branding context (customization US-003).
 *
 * Fetches /api/workspaces/<active>/branding whenever the active workspace
 * changes and applies the result to the live document:
 *
 *   1. Sets CSS custom properties (--brand-primary, --brand-secondary, --brand-font)
 *      on `document.documentElement` so CSS rules pick them up live.
 *   2. Replaces the favicon <link> tag.
 *   3. Updates document.title prefix to use the branded name.
 *
 * Brand-name and logoUrl are exposed via `useBranding()` so the
 * BrandMark component (and anyone else) can render them per-workspace.
 *
 * Falls back to platform defaults silently when:
 *   - no active workspace (loading state)
 *   - the /branding endpoint 404s
 *   - branding is empty
 *
 * `getBrand()` is the canonical way for non-React code to read the
 * current brand name; it returns the most-recently-applied branding
 * object so e-mail-template helpers + the like can use it.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api } from '../api';
import { useActiveWorkspace } from './ActiveWorkspaceContext';
import type { WorkspaceBranding } from '../types';

const PLATFORM_DEFAULT_NAME = 'Pipebase';

interface BrandingContextValue {
  branding: WorkspaceBranding;
  brandName: string;
  /** Re-fetch from the server. Useful after the settings UI saves. */
  refresh: () => Promise<void>;
}

const Ctx = createContext<BrandingContextValue>({
  branding: {},
  brandName: PLATFORM_DEFAULT_NAME,
  refresh: () => Promise.resolve(),
});

// Module-level snapshot for non-React code. Updated whenever the
// provider applies new branding.
let currentBranding: WorkspaceBranding = {};

/** Read the active workspace's brand name from anywhere — including
 *  non-React code. Returns the platform default when no override. */
export function getBrand(): { name: string; branding: WorkspaceBranding } {
  return {
    name: currentBranding.brandName ?? PLATFORM_DEFAULT_NAME,
    branding: currentBranding,
  };
}

function applyBrandingToDocument(b: WorkspaceBranding): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;

  // CSS variables — clear when unset so the platform palette wins.
  if (b.primaryColor) {
    root.style.setProperty('--brand-primary', b.primaryColor);
  } else {
    root.style.removeProperty('--brand-primary');
  }
  if (b.secondaryColor) {
    root.style.setProperty('--brand-secondary', b.secondaryColor);
  } else {
    root.style.removeProperty('--brand-secondary');
  }
  if (b.font) {
    root.style.setProperty('--brand-font', b.font);
  } else {
    root.style.removeProperty('--brand-font');
  }

  // Favicon — locate-or-create the <link rel="icon"> tag and patch it.
  const head = document.head;
  let favicon = head.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (b.faviconUrl) {
    if (!favicon) {
      favicon = document.createElement('link');
      favicon.rel = 'icon';
      head.appendChild(favicon);
    }
    favicon.href = b.faviconUrl;
    // Drop any platform-default type so the browser sniffs from the URL.
    favicon.removeAttribute('type');
  } else if (favicon && favicon.dataset.brandPatched === 'true') {
    // We patched it last time — restore the platform default.
    favicon.href = '/favicon.svg';
    favicon.type = 'image/svg+xml';
    delete favicon.dataset.brandPatched;
  }
  if (favicon && b.faviconUrl) {
    favicon.dataset.brandPatched = 'true';
  }

  // Title — prefix with brand name. Original title gets cached on the
  // body's data attribute so we can restore it cleanly.
  if (b.brandName) {
    if (!document.body.dataset.originalTitle) {
      document.body.dataset.originalTitle = document.title;
    }
    document.title = document.title.replace(/^[^—]+/, `${b.brandName} `);
  } else if (document.body.dataset.originalTitle) {
    document.title = document.body.dataset.originalTitle;
    delete document.body.dataset.originalTitle;
  }
}

export function WorkspaceBrandingProvider({ children }: { children: ReactNode }) {
  const { active } = useActiveWorkspace();
  const [branding, setBranding] = useState<WorkspaceBranding>({});

  const refresh = useCallback(async () => {
    if (!active) return;
    try {
      const b = await api.getWorkspaceBranding(active.id);
      setBranding(b ?? {});
    } catch {
      // 403 / 404 / network — fall back to platform defaults silently.
      setBranding({});
    }
  }, [active]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Apply to document whenever branding (or active workspace) changes.
  useEffect(() => {
    currentBranding = branding;
    applyBrandingToDocument(branding);
  }, [branding]);

  const value = useMemo<BrandingContextValue>(
    () => ({
      branding,
      brandName: branding.brandName ?? PLATFORM_DEFAULT_NAME,
      refresh,
    }),
    [branding, refresh],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useBranding(): BrandingContextValue {
  return useContext(Ctx);
}

/** Test seam: reset module state between tests. Not used by app code. */
export function __resetBrandingForTests(): void {
  currentBranding = {};
}
